import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';

const styles = require('./proxy_autocomplete.module.scss');

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceDetails {
  result?: {
    place_id: string;
    address_components: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    geometry?: {
      location: {
        lat: number;
        lng: number;
      };
    };
  };
}

interface Props {
  defaultValue?: string;
  id?: string;
  name?: string;
  onFocus?: () => void;
  onPlaceSelected: (place: any) => void;
  placeholder?: string;
  required?: boolean;
  types?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export default function ProxyAutocomplete({
  defaultValue = '',
  id,
  name,
  onFocus,
  onPlaceSelected,
  placeholder,
  required,
  types = '(cities)',
  inputRef: externalRef,
}: Props) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef || internalRef;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchPredictions = useCallback(
    debounce(async (input: string) => {
      if (!input || input.length < 2) {
        setPredictions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `/places/autocomplete?input=${encodeURIComponent(input)}&types=${encodeURIComponent(types)}`
        );
        const data = await response.json();
        setPredictions(data.predictions || []);
      } catch (error) {
        console.error('Error fetching predictions:', error); // eslint-disable-line no-console
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [types]
  );

  useEffect(() => {
    fetchPredictions(inputValue);
  }, [inputValue, fetchPredictions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  const handleSelect = async (prediction: Prediction) => {
    setInputValue(prediction.description);
    setShowDropdown(false);
    setPredictions([]);

    try {
      const response = await fetch(
        `/places/details?place_id=${encodeURIComponent(prediction.place_id)}`
      );
      const data: PlaceDetails = await response.json();

      if (data.result) {
        const place = {
          id: data.result.place_id,
          place_id: data.result.place_id,
          formatted_address: prediction.description,
          address_components: data.result.address_components,
          geometry: data.result.geometry
            ? {
                location: {
                  lat: () => data.result.geometry.location.lat,
                  lng: () => data.result.geometry.location.lng,
                },
              }
            : undefined,
        };
        onPlaceSelected(place);
      }
    } catch (error) {
      console.error('Error fetching place details:', error); // eslint-disable-line no-console
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(predictions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  return (
    <div className={styles.container}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={e => {
          setInputValue(e.target.value);
          setShowDropdown(true);
          setHighlightedIndex(-1);
        }}
        onFocus={() => {
          setShowDropdown(true);
          onFocus?.();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className={styles.input}
      />
      {showDropdown && (inputValue.length >= 2 || predictions.length > 0) && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {loading && <div className={styles.loading}>Loading...</div>}
          {!loading && predictions.length === 0 && inputValue.length >= 2 && (
            <div className={styles.noResults}>No results found</div>
          )}
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              className={`${styles.prediction} ${index === highlightedIndex ? styles.highlighted : ''}`}
              onClick={() => handleSelect(prediction)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {prediction.structured_formatting ? (
                <>
                  <span className={styles.mainText}>
                    {prediction.structured_formatting.main_text}
                  </span>
                  <span className={styles.secondaryText}>
                    {prediction.structured_formatting.secondary_text}
                  </span>
                </>
              ) : (
                prediction.description
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
